import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import { formatDateOnlyForDisplay } from "helpers/date";
import { Badge } from "primereact/badge";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useContext, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useGetAllBaseRateFilesQuery } from "store/queries/baseRateFiles";
import {
  useGetContractFeeSchedulesQuery,
  useDeleteContractFeeScheduleMutation,
  useRegenerateProceduresMutation,
  useActivateContractFeeScheduleMutation,
  useGenerateProviderProceduresMutation,
  ContractFeeSchedule,
  ContractFilterParams,
  IContractTermInGroup,
  normalizeRateType,
} from "store/queries/contractFeeSchedules";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetAllProvidersQuery } from "store/queries/providers";

const statusOptions = [
  { label: "All Statuses", value: "" },
  { label: "Draft", value: "Draft" },
  { label: "Active", value: "Active" },
  { label: "Inactive", value: "Inactive" },
  { label: "Expired", value: "Expired" },
  { label: "Importing", value: "Importing" },
  { label: "Outdated", value: "Outdated" },
];

const statusSeverityMap = {
  Draft: "info",
  Active: "success",
  Inactive: "warning",
  Expired: "danger",
  Importing: "warning",
} as const;

interface IContractsTableProps {
  accountId?: string; // Optional accountId to filter contracts for specific account
  showAddButton?: boolean; // Optional prop to show add contract button
}

function contractShowsOutdated(row: ContractFeeSchedule): boolean {
  if (row.proceduresOutdated) return true;
  // eslint-disable-next-line no-underscore-dangle
  return !!row._termsInGroup?.some(
    (t) => t.status === "Active" && t.proceduresOutdated
  );
}

export function ContractsTable({
  accountId,
  showAddButton = false,
}: IContractsTableProps = {}) {
  const navigate = useNavigate();
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);

  // Filter state
  const [filters, setFilters] = useState<ContractFilterParams>({
    search: "",
    status: "",
    providerId: "",
    payerId: undefined,
    ...(accountId && { accountId }), // Add accountId to filters if provided
  });

  // Data fetching
  const {
    data: contracts,
    isLoading,
    refetch,
  } = useGetContractFeeSchedulesQuery(filters);
  const { data: providers } = useGetAllProvidersQuery();
  const { data: payers } = useGetAllPayersQuery();
  const { data: baseRateFiles } = useGetAllBaseRateFilesQuery({});
  const [deleteContract] = useDeleteContractFeeScheduleMutation();
  const [regenerateProcedures] = useRegenerateProceduresMutation();
  const [activateContract] = useActivateContractFeeScheduleMutation();
  const [generateProcedures] = useGenerateProviderProceduresMutation();

  // Sort: provider name ASC, then payer name ASC
  const sortedContracts = useMemo(() => {
    const list = (contracts || []).slice();
    list.sort((a: ContractFeeSchedule, b: ContractFeeSchedule) => {
      const aProv = a.provider?.name?.toString() || "";
      const bProv = b.provider?.name?.toString() || "";
      const provCmp = aProv.localeCompare(bProv, undefined, {
        sensitivity: "base",
      });
      if (provCmp !== 0) return provCmp;
      const aPayer = a.payer?.name?.toString() || "";
      const bPayer = b.payer?.name?.toString() || "";
      return aPayer.localeCompare(bPayer, undefined, { sensitivity: "base" });
    });
    return list;
  }, [contracts]);

  // Provider and Payer options for dropdowns
  const providerOptions = useMemo(() => {
    const options = [{ label: "All Providers", value: "" }];
    if (providers?.data) {
      // If we're in account context, filter providers to only show those from this account
      let filteredProviders = providers.data;
      if (accountId) {
        filteredProviders = providers.data.filter(
          (p: any) => p.accountId === accountId
        );
      }
      options.push(
        ...filteredProviders.map((p) => ({ label: p.name, value: p.id }))
      );
    }
    return options;
  }, [providers, accountId]);

  const payerOptions = useMemo(() => {
    const options = [{ label: "All Payers", value: "" }];
    if (payers?.data) {
      options.push(
        ...payers.data.map((p: any) => ({
          label: p.name,
          value: p.id.toString(),
        }))
      );
    }
    return options;
  }, [payers]);

  const handleFilterChange = (
    field: keyof ContractFilterParams,
    value: any
  ) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      status: "",
      providerId: "",
      payerId: undefined,
      ...(accountId ? { accountId } : {}),
    });
  };

  const handleDelete = async (contract: ContractFeeSchedule) => {
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      `Are you sure you want to delete the contract for ${contract.provider?.name} - ${contract.payer?.name}?\n\nThis will permanently delete all provider procedures that were created from this contract for this provider and payer.`
    );

    if (confirmed) {
      try {
        setIsLoading(true);
        await deleteContract(contract.id).unwrap();
        toastElement?.show({
          severity: "success",
          summary: "Contract Deleted",
          detail:
            "Contract fee schedule and related provider procedures have been deleted successfully.",
        });
        refetch();
      } catch (error: any) {
        const errorMessage =
          error?.data?.messages?.[0] ||
          error?.data?.message ||
          "Failed to delete contract fee schedule.";
        toastElement?.show({
          severity: "error",
          summary: "Error",
          detail: errorMessage,
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleRegenerateProcedures = async (contract: ContractFeeSchedule) => {
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      `Are you sure you want to regenerate procedures for ${contract.provider?.name} - ${contract.payer?.name}?\n\nThis will replace all existing provider procedures with new ones based on the latest base rate files.`
    );

    if (confirmed) {
      try {
        setIsLoading(true);
        await regenerateProcedures(contract.id).unwrap();
        toastElement?.show({
          severity: "success",
          summary: "Procedures Regenerated",
          detail:
            "Provider procedures have been regenerated successfully with the latest base rates.",
        });
        refetch();
      } catch (error: any) {
        const errorMessage =
          error?.data?.messages?.[0] ||
          error?.data?.message ||
          "Failed to regenerate procedures.";
        toastElement?.show({
          severity: "error",
          summary: "Error",
          detail: errorMessage,
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleActivateContract = async (contract: ContractFeeSchedule) => {
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      `Activating the contract for ${contract.provider?.name} - ${contract.payer?.name} will overwrite existing provider fee schedules for this provider and payer.\n\nDo you want to continue?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setIsLoading(true);
      await activateContract(contract.id).unwrap();

      const result = await generateProcedures({
        contractId: contract.id,
        options: { overwriteExisting: true },
      }).unwrap();

      if (result.status === "processing") {
        toastElement?.show({
          severity: "info",
          summary: "Activation Started",
          detail:
            "The contract activation and procedure generation have started. The table will update once processing completes.",
          life: 10000,
        });
      } else {
        toastElement?.show({
          severity: "success",
          summary: "Contract Activated",
          detail:
            result.message ||
            "Contract activated and provider procedures generated successfully.",
        });
      }

      refetch();
    } catch (error: any) {
      const errorMessage =
        error?.data?.messages?.[0] ||
        error?.data?.message ||
        "Failed to activate contract.";
      toastElement?.show({
        severity: "error",
        summary: "Activation Failed",
        detail: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Table columns
  const providerPayerBodyTemplate = (rowData: ContractFeeSchedule) => {
    const contractUrl =
      accountId && showAddButton
        ? `/hospitals/view/${accountId}/contracts/${rowData.id}`
        : `/payer-rules/contracts/${rowData.id}`;

    // eslint-disable-next-line no-underscore-dangle
    const termCount = rowData._termCount;
    const termHint =
      termCount != null && termCount > 1 ? ` · ${termCount} terms` : "";
    // eslint-disable-next-line no-underscore-dangle
    const termsInGroup = rowData._termsInGroup;

    return (
      <Link
        to={contractUrl}
        state={{
          termsInGroup: termsInGroup as IContractTermInGroup[] | undefined,
          contractGroupId: rowData.contractGroupId,
        }}
        className="no-underline"
      >
        <div className="font-medium text-primary">
          {rowData.provider?.name} → {rowData.payer?.name}
          {termHint && (
            <span className="text-600 font-normal text-sm">{termHint}</span>
          )}
        </div>
      </Link>
    );
  };

  const statusBodyTemplate = (rowData: ContractFeeSchedule) => (
    <div className="flex align-items-center gap-2">
      <Badge
        value={rowData.status}
        severity={statusSeverityMap[rowData.status]}
      />
      {rowData.status === "Importing" && (
        <i className="pi pi-spin pi-spinner text-warning" />
      )}
      {contractShowsOutdated(rowData) && rowData.status === "Active" && (
        <span title="Base rates have been updated. Procedures need regeneration.">
          <Badge value="Outdated" severity="warning" className="ml-2" />
        </span>
      )}
    </div>
  );

  const dateBodyTemplate = (rowData: ContractFeeSchedule) => (
    <div>
      <div>Effective: {formatDateOnlyForDisplay(rowData.effectiveDate)}</div>
      {rowData.endDate && (
        <div className="text-sm text-500">
          End: {formatDateOnlyForDisplay(rowData.endDate)}
        </div>
      )}
      {rowData.nextUpdateDate && (
        <div className="text-sm text-500">
          Next Update: {formatDateOnlyForDisplay(rowData.nextUpdateDate)}
        </div>
      )}
    </div>
  );

  const ratesBodyTemplate = (rowData: ContractFeeSchedule) => {
    const getRateType = (calc: { rateType?: string }) =>
      normalizeRateType(calc.rateType);

    const formatRateCalculations = () => {
      if (!rowData.rateCalculations || rowData.rateCalculations.length === 0) {
        return "No rate calculations";
      }

      return rowData.rateCalculations
        .map((calc) => {
          const baseRateFile = baseRateFiles?.data?.find(
            (f) => f.id === calc.baseRateFileId
          );
          const baseFileName = baseRateFile?.displayName || "Base Rate";
          const rateType = getRateType(calc);

          if (rateType === "unit") {
            const dollarAmount = (calc.ratePercent / 100).toFixed(2);
            return `$${dollarAmount} per ${baseFileName}`;
          }
          return `${calc.ratePercent}% of ${baseFileName}`;
        })
        .join(", ");
    };

    return (
      <div className="text-sm">
        <div>{formatRateCalculations()}</div>
        {/* eslint-disable-next-line no-underscore-dangle */}
        {rowData._count && (
          <div className="text-500">
            {/* eslint-disable-next-line no-underscore-dangle */}
            {rowData._count.carveouts} carveouts
            {rowData.mpdrRulesetIds &&
              rowData.mpdrRulesetIds.length > 0 &&
              ", MPDR rules"}
            {rowData.caseRateRulesetIds &&
              rowData.caseRateRulesetIds.length > 0 &&
              ", Case rate rules"}
          </div>
        )}
      </div>
    );
  };

  const actionsBodyTemplate = (rowData: ContractFeeSchedule) => {
    // Don't render actions if there's no valid ID
    if (!rowData?.id) {
      return <div className="flex gap-2" />;
    }

    return (
      <div className="flex gap-2">
        {contractShowsOutdated(rowData) && rowData.status === "Active" && (
          <Button
            icon="pi pi-refresh"
            className="p-button-rounded p-button-text p-button-warning"
            onClick={() => handleRegenerateProcedures(rowData)}
            title="Regenerate procedures with latest base rates"
          />
        )}
        {rowData.status === "Draft" && (
          <Button
            icon="pi pi-check-circle"
            className="p-button-rounded p-button-text p-button-success"
            onClick={() => handleActivateContract(rowData)}
            title="Activate contract and generate procedures"
          />
        )}
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text"
          onClick={() => {
            const editUrl =
              accountId && showAddButton
                ? `/hospitals/view/${accountId}/contracts/${rowData.id}/edit`
                : `/payer-rules/contracts/${rowData.id}/edit`;
            // eslint-disable-next-line no-underscore-dangle
            const termsInGroupNav = rowData._termsInGroup;
            navigate(editUrl, {
              state: {
                termsInGroup: termsInGroupNav as
                  | IContractTermInGroup[]
                  | undefined,
                contractGroupId: rowData.contractGroupId,
              },
            });
          }}
        />
        <Button
          icon="pi pi-copy"
          className="p-button-rounded p-button-text"
          onClick={() => {
            const copyUrl =
              accountId && showAddButton
                ? `/hospitals/view/${accountId}/contracts/new?copyFrom=${rowData.id}`
                : `/payer-rules/contracts/new?copyFrom=${rowData.id}`;
            navigate(copyUrl);
          }}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger"
          onClick={() => handleDelete(rowData)}
        />
      </div>
    );
  };

  return (
    <>
      {/* Add Contract Button */}
      {showAddButton && (
        <div className="mb-3 flex justify-content-end">
          <Button
            label="New Contract"
            icon="pi pi-plus"
            className="p-button-raised"
            onClick={() => {
              if (accountId && showAddButton) {
                // Hospital context - use hospital-scoped route
                navigate(`/hospitals/view/${accountId}/contracts/new`);
              } else if (accountId) {
                // Admin context with account filter
                navigate(`/payer-rules/contracts/new?accountId=${accountId}`);
              } else {
                // Admin context without filter
                navigate("/payer-rules/contracts/new");
              }
            }}
          />
        </div>
      )}

      {/* Filters */}
      <div className="grid mb-4 p-3 border-1 border-300 border-round">
        <div className="col-12 md:col-3">
          <label className="block text-900 font-medium mb-2">Search</label>
          <InputText
            value={filters.search || ""}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            placeholder="Search contracts..."
            className="w-full"
          />
        </div>
        <div className="col-12 md:col-3">
          <label className="block text-900 font-medium mb-2">Status</label>
          <Dropdown
            value={filters.status}
            options={statusOptions}
            onChange={(e) => handleFilterChange("status", e.value)}
            placeholder="Select Status"
            className="w-full"
          />
        </div>
        <div className="col-12 md:col-3">
          <label className="block text-900 font-medium mb-2">Provider</label>
          <Dropdown
            value={filters.providerId}
            options={providerOptions}
            onChange={(e) => handleFilterChange("providerId", e.value)}
            placeholder="Select Provider"
            className="w-full"
            filter
          />
        </div>
        <div className="col-12 md:col-3">
          <label className="block text-900 font-medium mb-2">Payer</label>
          <Dropdown
            value={filters.payerId?.toString() || ""}
            options={payerOptions}
            onChange={(e) =>
              handleFilterChange(
                "payerId",
                e.value ? parseInt(e.value, 10) : undefined
              )
            }
            placeholder="Select Payer"
            className="w-full"
            filter
          />
        </div>
        <div className="col-12 flex justify-content-end">
          <Button
            label="Clear Filters"
            icon="pi pi-filter-slash"
            className="p-button-outlined"
            onClick={clearFilters}
          />
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        value={sortedContracts}
        loading={isLoading}
        paginator
        rows={25}
        rowsPerPageOptions={[10, 25, 50]}
        className="datatable-responsive"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} contracts"
        emptyMessage="No contract fee schedules found."
        rowHover
      >
        <Column
          header="Provider → Payer"
          body={providerPayerBodyTemplate}
          sortable
          style={{ minWidth: "250px" }}
        />
        <Column
          field="status"
          header="Status"
          body={statusBodyTemplate}
          sortable
          style={{ minWidth: "100px" }}
        />
        <Column
          field="effectiveDate"
          header="Dates"
          body={dateBodyTemplate}
          sortable
          style={{ minWidth: "200px" }}
        />
        <Column
          header="Configuration"
          body={ratesBodyTemplate}
          style={{ minWidth: "180px" }}
        />
        <Column
          header="Actions"
          body={actionsBodyTemplate}
          exportable={false}
          style={{ minWidth: "200px" }}
        />
      </DataTable>
    </>
  );
}

export default ContractsTable;
